<div *ngIf='discountRates.data() as itemTypes' class='pl-4'>
  <div class='container mx-auto theme-page-card-bg rounded-lg shadow-sm'>
    <!-- Header -->
    <nz-page-header [nzTitle]='titleTpl' class='site-page-header border-b'>
      <ng-template #titleTpl>
        <div class='flex items-center gap-3'>
          <span class='fe fe-percent text-primary text-3xl'></span>
          <div>
            <div class='text-2xl font-bold text-gray-800'>Discount Rates</div>
            <div class='text-sm text-gray-500 mt-1'>Manage discount rates for bookings and parcels</div>
          </div>
        </div>
      </ng-template>

      <nz-page-header-extra>
        <button 
          (click)='showAddModal=true' 
          class='btn btn-primary gap-2'>
          <span class='fe fe-plus'></span>
          New Discount Rate
        </button>
      </nz-page-header-extra>
    </nz-page-header>

    <!-- Empty State -->
    <section
      *ngIf='!itemTypes.length'
      class='max-w-2xl px-4 py-16 mx-auto text-center'
    >
      <div class='mb-6'>
        <span class='fe fe-percent text-gray-300' style='font-size: 80px;'></span>
      </div>
      <h2 class='text-2xl font-bold text-gray-800 mb-3'>
        No Discount Rates Yet
      </h2>
      <p class='text-gray-600 mb-8 max-w-md mx-auto'>
        Create discount rates to offer special pricing for bookings and parcels. 
        Discounts can be applied manually during transactions or via discount codes.
      </p>
      <button 
        (click)='showAddModal=true' 
        class='btn btn-primary btn-lg gap-2'>
        <span class='fe fe-plus'></span>
        Create Your First Discount
      </button>
    </section>

    <!-- Table with Data -->
    <section *ngIf='itemTypes.length' class='px-6 pb-6'>
      <div class='theme-page-card-bg rounded-lg border theme-border'>
        <nz-table 
          #basicTable 
          [nzData]='itemTypes' 
          [nzLoading]='discountRates.isLoading()'
          [nzShowPagination]='itemTypes.length > 10'
          [nzPageSize]='10'
          class='modern-table'>
          <thead class='bg-gray-50'>
            <tr>
              <th class='font-semibold text-gray-700'>Name</th>
              <th class='font-semibold text-gray-700'>Code</th>
              <th class='font-semibold text-gray-700'>Rate</th>
              <th class='font-semibold text-gray-700'>Applicable To</th>
              <th class='font-semibold text-gray-700'>Status</th>
              <th class='font-semibold text-gray-700'>Created</th>
              <th class='font-semibold text-gray-700'>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor='let data of itemTypes' class='theme-hover-bg transition-colors'>
              <!-- Name & Description -->
              <td>
                <div class='font-semibold text-gray-900'>{{ data.name }}</div>
                <div *ngIf='data.description' class='text-sm text-gray-500 mt-1'>
                  {{ data.description }}
                </div>
              </td>

              <!-- Code -->
              <td>
                <span *ngIf='data.code' class='badge badge-outline badge-sm font-mono'>
                  {{ data.code }}
                </span>
                <span *ngIf='!data.code' class='text-gray-400 text-sm'>—</span>
              </td>

              <!-- Rate -->
              <td>
                <div class='flex items-center gap-2'>
                  <span class='text-2xl font-bold text-primary'>{{ data.rate }}</span>
                  <span class='text-gray-500'>%</span>
                </div>
              </td>

              <!-- Applicable To -->
              <td>
                <div class='flex flex-col gap-1'>
                  <span 
                    *ngIf='data.applicableToBookings' 
                    class='badge badge-info badge-sm gap-1'>
                    <span class='fe fe-check-circle'></span>
                    Bookings
                  </span>
                  <span 
                    *ngIf='data.applicableToParcels' 
                    class='badge badge-warning badge-sm gap-1'>
                    <span class='fe fe-check-circle'></span>
                    Parcels
                  </span>
                </div>
              </td>

              <!-- Status -->
              <td>
                <span 
                  *ngIf='data.isActive' 
                  class='badge badge-success gap-1'>
                  <span class='fe fe-check'></span>
                  Active
                </span>
                <span 
                  *ngIf='!data.isActive' 
                  class='badge badge-ghost gap-1'>
                  <span class='fe fe-x'></span>
                  Inactive
                </span>
              </td>

              <!-- Created Date -->
              <td class='text-sm text-gray-600'>
                {{ data.createdAt | date: 'MMM d, y' }}
              </td>

              <!-- Actions -->
              <td>
                <div class='flex gap-2'>
                  <button 
                    (click)='showEditModal = true; selected$$.next(data)'
                    class='btn btn-sm btn-ghost gap-1 hover:bg-blue-50 hover:text-blue-600'>
                    <span class='fe fe-edit-2'></span>
                    Edit
                  </button>
                  <button 
                    (click)='delete(data)'
                    class='btn btn-sm btn-ghost gap-1 hover:bg-red-50 hover:text-red-600'>
                    <span class='fe fe-trash-2'></span>
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </nz-table>
      </div>
    </section>
  </div>

  <!-- Add Modal -->
  <nz-modal
    (nzOnCancel)='showAddModal = false'
    (nzOnOk)='add()'
    [nzClosable]='true'
    [nzCloseIcon]='closeIconTpl'
    [nzOkDisabled]='form.invalid || addDiscountRate.isPending()'
    [nzOkLoading]='addDiscountRate.isPending()'
    [nzVisible]='showAddModal'
    nzOkText='Create Discount'
    [nzWidth]='600'
  >
    <ng-template #closeIconTpl>
      <div class='mt-4'>
        <span class='fe fe-x'></span>
      </div>
    </ng-template>
    <div *nzModalTitle class='flex items-center gap-2'>
      <span class='fe fe-plus-circle text-primary'></span>
      <span>Add New Discount Rate</span>
    </div>
    <div *nzModalContent>
      <app-error-display 
        *ngIf="showError() && errorDetails" 
        [error]="errorDetails" 
        (dismiss)="onDismissError()" 
        (retry)="add()">
      </app-error-display>
      
      <form [formGroup]='form' nz-form nzLayout='vertical' class='space-y-4'>
        <!-- Name -->
        <nz-form-item>
          <nz-form-label nzRequired class='font-semibold'>Discount Name</nz-form-label>
          <nz-form-control nzErrorTip='Please enter a discount name'>
            <input
              formControlName='name'
              nz-input
              placeholder='e.g., Student Discount, Senior Citizen'
              type='text'
            />
          </nz-form-control>
        </nz-form-item>

        <!-- Description -->
        <nz-form-item>
          <nz-form-label class='font-semibold'>Description</nz-form-label>
          <nz-form-control>
            <textarea
              formControlName='description'
              nz-input
              rows='3'
              placeholder='Optional description for this discount'
            ></textarea>
          </nz-form-control>
        </nz-form-item>

        <!-- Code and Rate Row -->
        <div class='grid grid-cols-2 gap-4'>
          <!-- Code -->
          <nz-form-item>
            <nz-form-label class='font-semibold'>
              Discount Code
              <span class='text-xs text-gray-500 ml-1'>(Optional)</span>
            </nz-form-label>
            <nz-form-control nzErrorTip='Use only uppercase letters, numbers, - and _'>
              <input
                formControlName='code'
                nz-input
                placeholder='e.g., STUDENT10'
                type='text'
                style='text-transform: uppercase;'
                maxlength='20'
              />
              <div class='text-xs text-gray-500 mt-1'>For future voucher-based discounts</div>
            </nz-form-control>
          </nz-form-item>

          <!-- Rate -->
          <nz-form-item>
            <nz-form-label nzRequired class='font-semibold'>Discount Rate (%)</nz-form-label>
            <nz-form-control nzErrorTip='Please enter a valid rate'>
              <nz-input-group nzSuffix='%'>
                <input
                  [decimal]='true'
                  decimalSeparator='.'
                  digitOnly
                  formControlName='rate'
                  nz-input
                  pattern='^\d+(\.\d{1,2})?$'
                  placeholder='0.00'
                  type='text'
                />
              </nz-input-group>
            </nz-form-control>
          </nz-form-item>
        </div>

        <!-- Applicability -->
        <nz-form-item>
          <nz-form-label class='font-semibold'>Apply Discount To</nz-form-label>
          <nz-form-control>
            <div class='space-y-2'>
              <label nz-checkbox formControlName='applicableToBookings'>
                <span class='ml-2'>Seat Bookings</span>
              </label>
              <br />
              <label nz-checkbox formControlName='applicableToParcels'>
                <span class='ml-2'>Parcel Deliveries</span>
              </label>
            </div>
            <div class='text-xs text-gray-500 mt-2'>
              Select where this discount can be applied (for future integration)
            </div>
          </nz-form-control>
        </nz-form-item>
      </form>
    </div>
  </nz-modal>

  <!-- Edit Modal -->
  <tenant-edit-discount-rate
    (hide)='showEditModal = false; discountRates.refetch()'
    [rate]='(selected$$|async)!'
    [show]='showEditModal'>
  </tenant-edit-discount-rate>
</div>
